import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { User, Clock, TrendingUp, Target, BarChart3, Sparkles, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { computeTeamDataFromWorklogs, getUtilizationColor, buildProjectCapacityIndex } from './utils/teamPerformanceUtils';
import { getStatusColor } from './utils/styleUtils';
import { reportsService } from '../../../services/reportsService';
import { projectService } from '../../../services/projectService';
import { devCapacityService } from '../../../services/devCapacityService';
import { toast } from 'react-toastify';
import { AlertTriangle } from 'lucide-react';

// Module-level cache so the Team Performance tab restores instantly on
// Dashboard remount instead of recomputing/re-fetching the AI summary.
const teamPerformanceCache = {
  key: null,
  teamMetrics: null,
  aiSummary: null,
  includeAISummaries: false,
  expandedMembers: {},
};

const TeamPerformanceAnalysis = ({ startDate, endDate, filters, worklogs = [], worklogsLoaded = false, accountId = null }) => {
  // Cheap fingerprint: filters + worklogs length + first/last keys.
  // Avoids JSON.stringify on the full worklogs array (can be thousands of entries).
  const worklogsFingerprint = useMemo(() => {
    if (!worklogs || worklogs.length === 0) return '0';
    const first = worklogs[0];
    const last = worklogs[worklogs.length - 1];
    return `${worklogs.length}::${first?.worklog_id ?? first?.issue_key ?? ''}::${last?.worklog_id ?? last?.issue_key ?? ''}`;
  }, [worklogs]);

  const dataKey = useMemo(
    () => JSON.stringify({ accountId, startDate, endDate, filters, worklogsFingerprint, worklogsLoaded }),
    [accountId, startDate, endDate, filters, worklogsFingerprint, worklogsLoaded]
  );

  const cacheHit = teamPerformanceCache.key === dataKey;

  const [teamMetrics, setTeamMetrics] = useState(
    () => (cacheHit ? teamPerformanceCache.teamMetrics : null)
  );
  const [aiSummaryCache, setAiSummaryCache] = useState(
    () => (cacheHit ? teamPerformanceCache.aiSummary : null)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeAISummaries, setIncludeAISummaries] = useState(
    () => (cacheHit ? teamPerformanceCache.includeAISummaries : false)
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiAbortController = useRef(null);
  const [expandedMembers, setExpandedMembers] = useState(
    () => (cacheHit ? teamPerformanceCache.expandedMembers : {})
  );
  // Seed prevDataKeyRef from the cache so the data-refresh effect does NOT
  // recompute metrics when we already restored them from cache.
  const prevDataKeyRef = useRef(cacheHit ? dataKey : '');

  // Mirror state into the module cache so a remount restores it.
  useEffect(() => {
    teamPerformanceCache.key = dataKey;
    teamPerformanceCache.teamMetrics = teamMetrics;
    teamPerformanceCache.aiSummary = aiSummaryCache;
    teamPerformanceCache.includeAISummaries = includeAISummaries;
    teamPerformanceCache.expandedMembers = expandedMembers;
  }, [dataKey, teamMetrics, aiSummaryCache, includeAISummaries, expandedMembers]);
  const fetchAISummariesIfAvailable = useCallback(async (computed, forceRefresh = false) => {
    try {
      if (aiAbortController.current) {
        try { aiAbortController.current.abort(); } catch (e) { /* ignore */ }
      }
      const controller = new AbortController();
      aiAbortController.current = controller;

      setAiLoading(true);
      setAiError(null);

      const result = await reportsService.fetchTeamPerformanceAISummary(
        { startDate, endDate, filters, teamData: computed, forceRefresh },
        controller.signal
      );

      const summary = result.data?.ai_summary;
      if (summary && !summary.executive_summary?.includes("Unable to generate")) {
        setAiSummaryCache(summary);
        toast.success('AI Performance Analysis generated successfully!');
      } else {
        const errorMsg = 'Not available. Please try again.';
        setAiError(errorMsg);
        setAiSummaryCache(null);
        toast.error(errorMsg);
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      console.debug('AI summary failed:', err.message);
      const errorMsg = 'Failed to contact AI summary service.';
      setAiError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setAiLoading(false);
      aiAbortController.current = null;
    }
  }, [startDate, endDate, filters]);

  useEffect(() => {
    let canceled = false;
    const isDataRefresh = prevDataKeyRef.current !== dataKey;

    if (isDataRefresh && worklogsLoaded) {
      prevDataKeyRef.current = dataKey;
      setLoading(true);
      setError(null);
      (async () => {
        try {
          const filteredProjectKeys = Array.isArray(filters?.project) ? filters.project : [];
          let capacityIndex = null;
          if (filteredProjectKeys.length > 0) {
            try {
              const allTabProjects = await projectService.getProjects();
              const tabProjectsByKey = new Map();
              (allTabProjects || []).forEach(p => {
                if (p.jira_project_key) tabProjectsByKey.set(p.jira_project_key, p);
              });

              const developersByProjectId = {};
              const failedProjectKeys = [];
              await Promise.all(
                filteredProjectKeys.map(async (key) => {
                  const tabProject = tabProjectsByKey.get(key);
                  if (!tabProject) return;
                  try {
                    const devs = await devCapacityService.getProjectDevelopers(tabProject.id);
                    developersByProjectId[tabProject.id] = devs || [];
                  } catch (e) {
                    console.error(`[TeamPerf] Failed to fetch developers for project ${key} (id ${tabProject.id}):`, e);
                    developersByProjectId[tabProject.id] = [];
                    failedProjectKeys.push(key);
                  }
                })
              );

              capacityIndex = buildProjectCapacityIndex(
                filteredProjectKeys,
                allTabProjects,
                developersByProjectId,
                startDate,
                endDate,
                failedProjectKeys
              );
            } catch (e) {
              console.error('[TeamPerf] Failed to fetch capacity data:', e);
              capacityIndex = null;
            }
          }

          const metrics = computeTeamDataFromWorklogs(worklogs, startDate, endDate, capacityIndex);
          if (canceled) return;

          setTeamMetrics(metrics);
          setAiSummaryCache(null);
          setIncludeAISummaries(false);
          setAiError(null);
          setAiLoading(false);
          if (aiAbortController.current) {
            try { aiAbortController.current.abort(); } catch (e) { /* ignore */ }
            aiAbortController.current = null;
          }
        } catch (err) {
          if (canceled) return;
          console.error('[TeamPerf] Outer error:', err);
          setError(err.message || String(err));
        } finally {
          if (!canceled) setLoading(false);
        }
      })();
    }
    else if (isDataRefresh && !worklogsLoaded) {
      if (!startDate || !endDate) {
        // No date range — nothing to wait for. Advance ref and clear.
        prevDataKeyRef.current = dataKey;
        setTeamMetrics(null);
        setError(null);
        setLoading(false);
      } else {
        // Worklogs not ready yet — wait. Do NOT advance prevDataKeyRef so the
        // effect re-runs the fetch branch once worklogsLoaded flips to true.
        // Keep loading=true so the user sees a spinner instead of an empty state.
        setTeamMetrics(null);
        setError(null);
        setLoading(true);
      }
    }
    else if (includeAISummaries && teamMetrics && !aiSummaryCache && !aiLoading && !aiError) {
      fetchAISummariesIfAvailable(teamMetrics);
    }
    else if (!includeAISummaries) {
      if (aiError) {
        setAiError(null);
      }
      if (aiAbortController.current) {
        try { aiAbortController.current.abort(); } catch (e) { /* ignore */ }
        aiAbortController.current = null;
        setAiLoading(false);
      }
    }

    return () => { canceled = true; };
  }, [dataKey, includeAISummaries, fetchAISummariesIfAvailable, teamMetrics, aiSummaryCache, aiLoading, aiError, worklogsLoaded, startDate, endDate, worklogs]);
  // Consolidate the base metrics and the AI summary for display
  const mergedData = useMemo(() => {
    if (!teamMetrics) return null;
    return {
      ...teamMetrics,
      ai_summary: aiSummaryCache,
      team_members: teamMetrics.team_members.map(m => {
        const aiMember = aiSummaryCache?.contributors?.find(c => c.name === m.name);
        return {
          ...m,
          ai_summary: aiMember
        };
      })
    };
  }, [teamMetrics, aiSummaryCache]);

  const toggleMemberExpansion = (memberName) => {
    setExpandedMembers(prev => ({
      ...prev,
      [memberName]: !prev[memberName]
    }));
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 sm:p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 dark:text-blue-400 mb-4" />
        <p className="text-gray-600 dark:text-slate-300">Generating team performance analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="text-red-600 dark:text-red-400">
          <p className="font-medium">Error loading team performance</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!mergedData || !mergedData.team_members || mergedData.team_members.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 sm:p-8 text-center">
        <p className="text-gray-600 dark:text-slate-300">No team performance data available for the selected period.</p>
      </div>
    );
  }

  // Prepare chart data
  const chartData = mergedData.team_members.map(member => ({
    name: member.name,
    logged: member.total_hours,
    available: member.available_hours,
    utilization: member.utilization_percentage
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-100">Team Performance Analysis</h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-slate-400 mt-1">
              {mergedData.period.start_date} to {mergedData.period.end_date}
              {' • '}
              {mergedData.period.working_days} working days
              {' • '}
              {mergedData.team_members.length} team members
            </p>
            {mergedData.capacity_warning && (mergedData.capacity_warning.no_filter || mergedData.capacity_warning.fallback_projects.length > 0 || (mergedData.capacity_warning.failed_projects || []).length > 0) && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-300 space-y-1">
                  {mergedData.capacity_warning.no_filter && (
                    <div>No project filter applied — using default 40h/week per member. Filter by a project from the Projects tab to use real capacity data.</div>
                  )}
                  {mergedData.capacity_warning.fallback_projects.length > 0 && (
                    <div>
                      Using default 40h/week for {mergedData.capacity_warning.fallback_projects.length === 1 ? 'project' : 'projects'}{' '}
                      <strong>{mergedData.capacity_warning.fallback_projects.join(', ')}</strong>{' '}
                      (not configured in the Projects tab). Other selected projects use capacity data.
                    </div>
                  )}
                  {(mergedData.capacity_warning.failed_projects || []).length > 0 && (
                    <div>
                      Failed to load capacity for {mergedData.capacity_warning.failed_projects.length === 1 ? 'project' : 'projects'}{' '}
                      <strong>{mergedData.capacity_warning.failed_projects.join(', ')}</strong>{' '}
                      — falling back to default 40h/week. Check the console for details and try again.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              if (aiError) {
                setAiError(null);
                fetchAISummariesIfAvailable(teamMetrics, true);
                setIncludeAISummaries(true);
                return;
              }
              setIncludeAISummaries(!includeAISummaries);
            }}
            disabled={aiLoading}
            className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium w-full sm:w-auto ${aiLoading
              ? 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 cursor-not-allowed'
              : includeAISummaries
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                : 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 shadow-sm hover:shadow'
              }`}
            title={aiError ? 'Click to retry' : ''}
          >
            {aiLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
            ) : (
              <Sparkles className={`w-5 h-5 ${includeAISummaries ? 'text-blue-600 dark:text-blue-400' : 'text-white'}`} />
            )}
            <span>
              {aiLoading
                ? 'Generating Analysis...'
                : !aiSummaryCache
                  ? (aiError ? 'Try Again' : 'Generate AI Analysis')
                  : includeAISummaries
                    ? 'Hide AI Analysis'
                    : 'Show AI Analysis'}
            </span>
          </button>
        </div>

        {/* Overall Team Summary */}
        {includeAISummaries && mergedData.ai_summary && (
          <div className="mt-6 space-y-4">

            {/* Executive Summary */}
            <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-900 dark:text-blue-200 mb-1">
                    AI Executive Summary
                  </h3>
                  <p className="text-sm sm:text-base text-blue-800 dark:text-blue-300">
                    {mergedData.ai_summary.executive_summary || "No executive summary available."}
                  </p>
                </div>
              </div>
            </div>

            {/* Contributors */}
            <div className="p-3 sm:p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg">
              <h4 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-slate-200 mb-3">
                Individual Contributions
              </h4>

              <div className="space-y-3">
                {(mergedData.ai_summary.contributors || []).map((c) => {
                  const util = Number(c.utilization_percentage || 0);
                  return (
                    <div
                      key={c.name}
                      className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md border border-gray-200 dark:border-white/10"
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline flex-wrap gap-2">
                            <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-slate-100">
                              {c.name || "Unknown"}
                            </span>
                            {c.role && (
                              <span className="text-xs sm:text-sm px-2 py-0.5 bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-200 rounded">
                                {c.role}
                              </span>
                            )}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 dark:text-slate-400">
                            {typeof c.total_hours === 'number' && (
                              <span className="mr-2">{c.total_hours}h logged</span>
                            )}
                            {typeof c.available_hours === 'number' && (
                              <span className={`inline-flex items-center gap-1 ${!c.available_hours ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}`}>
                                {c.available_hours}h available
                                {!c.available_hours && (
                                  <AlertTriangle className="w-3 h-3 inline" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm sm:text-base text-gray-600 dark:text-slate-300 font-medium">
                          {util.toFixed(1)}%
                        </div>
                      </div>

                      <p className="text-base text-gray-700 dark:text-slate-300 mb-2">
                        {c.summary || "No summary available."}
                      </p>

                      {/* Top Issues (if provided by AI) */}
                      {(c.issues || []).length > 0 && (
                        <div className="text-sm text-gray-700 dark:text-slate-300 mb-2">
                          <div className="font-medium text-base text-gray-800 dark:text-slate-200 mb-1">Top issues</div>
                          <ul className="list-disc pl-5">
                            {(c.issues || []).map((issue, idx) => (
                              <li key={idx}>
                                <span className="font-medium">{issue.key}</span>
                                {issue.logged_hours != null && (
                                  <span className="text-gray-500 dark:text-slate-400"> — {issue.logged_hours}h</span>
                                )}
                                {issue.summary && <div className="text-gray-600 dark:text-slate-400">{issue.summary}</div>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Strengths / Areas / Actions */}
                      <div className="flex flex-wrap gap-2">
                        {[].concat(c.strengths || []).map((s, i) => (
                          <span key={`s-${i}`} className="px-2 py-0.5 text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded">{s}</span>
                        ))}
                        {[].concat(c.areas_for_improvement || []).map((a, i) => (
                          <span key={`a-${i}`} className="px-2 py-0.5 text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded">{a}</span>
                        ))}
                        {[].concat(c.recommended_actions || []).map((r, i) => (
                          <span key={`r-${i}`} className="px-2 py-0.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">{r}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trends & Concerns */}
            {((mergedData.ai_summary.positive_trends || []).length > 0 ||
              (mergedData.ai_summary.concerns_risks || []).length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {(mergedData.ai_summary.positive_trends || []).length > 0 && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <h4 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
                        Positive Trends
                      </h4>
                      <ul className="list-disc pl-5 text-base text-green-800 dark:text-green-300 space-y-1">
                        {[].concat(mergedData.ai_summary.positive_trends || []).map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(mergedData.ai_summary.concerns_risks || []).length > 0 && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                        Concerns & Risks
                      </h4>
                      <ul className="list-disc pl-5 text-base text-yellow-800 dark:text-yellow-300 space-y-1">
                        {[].concat(mergedData.ai_summary.concerns_risks || []).map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
          </div>
        )}


      </div>

      {/* Utilization Flow Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 sm:p-6">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <span className="min-w-0">Hours Utilization: Logged vs Available</span>
        </h3>
        <p className="text-sm sm:text-base text-gray-600 dark:text-slate-400 mb-4">
          Available hours calculated as {mergedData.period.working_days} working days × 8 hours/day = {mergedData.available_hours.toFixed(1)} hours
        </p>
        <div className="w-full h-[300px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 14 }}
              />
              <YAxis
                label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'logged') return [`${value.toFixed(1)}h`, 'Logged Hours'];
                  if (name === 'available') return [`${value.toFixed(1)}h`, 'Available Hours'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar dataKey="available" fill="#659848" name="Available Hours" />
              <Bar dataKey="logged" name="Logged Hours">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getUtilizationColor(entry.utilization)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex items-center justify-center flex-wrap gap-2 sm:gap-4 md:gap-6 text-xs sm:text-sm text-gray-700 dark:text-slate-300">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>&lt; 60% (Underutilized)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span>60-80% (Normal)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>80-100% (Good)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-amber-500 rounded"></div>
            <span>&gt; 100% (Overworked)</span>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="space-y-4">
        {mergedData.team_members.map((member) => (
          <div key={member.name} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
            <div
              className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              onClick={() => toggleMemberExpansion(member.name)}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-slate-100">{member.name}</h3>
                    <div className="flex items-center flex-wrap gap-2 sm:gap-4 mt-1 text-xs sm:text-sm md:text-base text-gray-600 dark:text-slate-300">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        {member.total_hours.toFixed(1)}h logged
                      </span>
                      <span className={`flex items-center ${!member.available_hours ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                        <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        {member.utilization_percentage.toFixed(1)}% utilization
                        {!member.available_hours && (
                          <span className="ml-1 text-xs">(0h available)</span>
                        )}
                      </span>
                      <span className="flex items-center">
                        <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        {member.issues_worked_on} issues
                      </span>
                      {member.estimation_accuracy && (
                        <span className="flex items-center">
                          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {member.estimation_accuracy.accuracy_percentage.toFixed(1)}% accuracy
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 sm:ml-4">
                  <div className="w-full sm:w-24 h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${Math.min(member.utilization_percentage, 100)}%`,
                        backgroundColor: getUtilizationColor(member.utilization_percentage)
                      }}
                    ></div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-1 text-center sm:text-center">
                    {member.utilization_percentage.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            {expandedMembers[member.name] && (
              <div className="border-t border-gray-200 dark:border-white/10 p-3 sm:p-4 bg-gray-50 dark:bg-slate-700/30">

                {/* Zero available hours warning */}
                {!member.available_hours && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      Available hours are <strong>0</strong> for this member — utilization and accuracy percentages may be inaccurate. Check their capacity settings in the Projects tab.
                    </p>
                  </div>
                )}

                {/* AI Summary */}
                {includeAISummaries && member.ai_summary && (
                  <div className="mb-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div className="w-full min-w-0">
                        <h4 className="font-semibold text-blue-900 dark:text-blue-200 text-sm sm:text-base mb-1">
                          AI Work Summary
                        </h4>

                        <p className="text-sm sm:text-base text-blue-800 dark:text-blue-300 mb-3">
                          {member.ai_summary.summary || "No summary available."}
                        </p>

                        {/* Focus Areas */}
                        {(member.ai_summary.focus_areas || []).length > 0 && (
                          <div className="mb-2">
                            <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                              Focus Areas
                            </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(member.ai_summary.focus_areas || []).map((f, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded"
                                >
                                  {f}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Patterns */}
                        {(member.ai_summary.patterns || []).length > 0 && (
                          <ul className="mt-2 list-disc pl-4 text-sm text-blue-800 dark:text-blue-300 space-y-1">
                            {(member.ai_summary.patterns || []).map((p, i) => (
                              <li key={i}>{p}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Issues Worked On */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-slate-100 text-sm sm:text-base mb-2">Issues Worked On ({member.issues.length})</h4>
                  <div className="space-y-2">
                    {member.issues.map((issue, idx) => (
                      <div key={idx} className="bg-white dark:bg-slate-800 rounded p-2 sm:p-3 border border-gray-200 dark:border-white/10">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2">
                              <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-slate-100">{issue.key}</span>
                              <span className={`text-xs sm:text-sm px-2 py-1 rounded ${getStatusColor(issue.status)}`}>{issue.status}</span>
                              {issue.priority && (
                                <span className="text-xs sm:text-sm px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded">{issue.priority}</span>
                              )}
                            </div>
                            <p className="text-sm sm:text-base text-gray-700 dark:text-slate-300 mt-1">{issue.summary}</p>
                            <div className="flex items-center flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-600 dark:text-slate-400">
                              <span>Logged: {issue.logged_hours.toFixed(1)}h</span>
                              {issue.estimated_hours > 0 && (
                                <>
                                  <span>Estimated: {issue.estimated_hours.toFixed(1)}h</span>
                                  <span className={issue.variance >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                                    Variance: {issue.variance >= 0 ? '+' : ''}{issue.variance.toFixed(1)}h
                                  </span>
                                </>
                              )}
                            </div>
                            {issue.work_descriptions && issue.work_descriptions.length > 0 && (
                              <div className="mt-2">
                                <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Work Done:</p>
                                <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                                  {issue.work_descriptions.map((desc, descIdx) => (
                                    <li key={descIdx} className="flex items-start">
                                      <span className="mr-2">•</span>
                                      <span>{desc}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Metrics */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                  <div className="bg-white dark:bg-slate-800 rounded p-2 sm:p-3 border border-gray-200 dark:border-white/10">
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">Worklog Entries</p>
                    <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-slate-100">{member.worklog_entries}</p>
                  </div>
                  {member.estimation_accuracy && (
                    <>
                      <div className="bg-white dark:bg-slate-800 rounded p-2 sm:p-3 border border-gray-200 dark:border-white/10">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">Estimation Accuracy</p>
                        <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-slate-100">
                          {member.estimation_accuracy.accuracy_percentage.toFixed(1)}%
                        </p>
                      </div>
                      <div className="bg-white dark:bg-slate-800 rounded p-2 sm:p-3 border border-gray-200 dark:border-white/10">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">Total Variance</p>
                        <p className={`text-lg sm:text-xl font-semibold ${member.estimation_accuracy.variance >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {member.estimation_accuracy.variance >= 0 ? '+' : ''}{member.estimation_accuracy.variance.toFixed(1)}h
                        </p>
                      </div>
                    </>
                  )}
                  <div className="bg-white dark:bg-slate-800 rounded p-2 sm:p-3 border border-gray-200 dark:border-white/10">
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">Collaboration Rate</p>
                    <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-slate-100">{member.collaboration_rate.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamPerformanceAnalysis;




